import React, { useEffect } from 'react';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import LandingPage from './pages/LandingPage';
import SectionPage from './pages/SectionPage';
import CatalogPage from './pages/CatalogPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import OrdersAdminPage from './pages/OrdersAdminPage';
import AffiliatesAdminPage from './pages/AffiliatesAdminPage';
import InventoryAdminPage from './pages/InventoryAdminPage';
import GarmentDetailPage from './pages/GarmentDetailPage';
import IkigaiPage from './pages/IkigaiPage';
import TrackOrderPage from './pages/TrackOrderPage';
import GiftCardsPage from './pages/GiftCardsPage';
import GiftCardSuccessPage from './pages/GiftCardSuccessPage';
import LegalNoticePage from './pages/LegalNoticePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import ReturnsPage from './pages/ReturnsPage';
import SizeGuidePage from './pages/SizeGuidePage';
import LoyaltyPage from './pages/LoyaltyPage';
import CustomerDashboardPage from './pages/CustomerDashboardPage';
import WishlistPage from './pages/WishlistPage';
import ChatBubble from './components/ChatBubble';
import CookieBanner from './components/CookieBanner';
import './App.css';

// Sincronizar modo admin desde URL a localStorage al arrancar la app
// (ANTES de que se rendericen los componentes o se hagan peticiones)
try {
  const params = new URLSearchParams(window.location.search);
  const paramToken = params.get('admin');
  if (paramToken && paramToken !== 'off') {
    localStorage.setItem('jrf-admin-token', paramToken);
    localStorage.setItem('jrf-admin', 'true');
  }
  if (paramToken === 'off') {
    localStorage.removeItem('jrf-admin-token');
    localStorage.removeItem('jrf-admin');
  }
} catch (e) { /* ignore */ }

// Interceptor global de axios: añade X-Admin-Token en cada petición si está activo
axios.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('jrf-admin-token');
    if (token) {
      config.headers['X-Admin-Token'] = token;
    }
  } catch (e) { /* ignore localStorage errors */ }
  return config;
});

// Reintenta GET si el servidor no responde un momento (p. ej. mientras se publica la web)
axios.interceptors.response.use(undefined, async (error) => {
  const config = error.config;
  const status = error.response?.status;
  const retriable = !error.response || status === 502 || status === 503 || status === 504 || status === 520;
  if (!config || (config.method || 'get') !== 'get' || !retriable) throw error;
  config.__retry = (config.__retry || 0) + 1;
  if (config.__retry > 3) throw error;
  await new Promise((r) => setTimeout(r, 1000 * config.__retry));
  return axios(config);
});

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <CartProvider>
        <WishlistProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/mujer" element={<SectionPage />} />
              <Route path="/rebajas" element={<SectionPage />} />
              <Route path="/hombre" element={<SectionPage />} />
              <Route path="/accesorios" element={<SectionPage />} />
              <Route path="/catalogo" element={<CatalogPage />} />
              <Route path="/product/:id" element={<GarmentDetailPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
              <Route path="/track" element={<TrackOrderPage />} />
              <Route path="/gift-cards" element={<GiftCardsPage />} />
              <Route path="/gift-cards/success" element={<GiftCardSuccessPage />} />
              {/* Customer Pages */}
              <Route path="/mi-cuenta" element={<CustomerDashboardPage />} />
              <Route path="/wishlist" element={<WishlistPage />} />
              <Route path="/fidelidad" element={<LoyaltyPage />} />
              {/* Legal Pages */}
              <Route path="/aviso-legal" element={<LegalNoticePage />} />
              <Route path="/politica-privacidad" element={<PrivacyPolicyPage />} />
              <Route path="/terminos-condiciones" element={<TermsConditionsPage />} />
              <Route path="/politica-cookies" element={<CookiePolicyPage />} />
              <Route path="/devoluciones" element={<ReturnsPage />} />
              <Route path="/guia-tallas" element={<SizeGuidePage />} />
              {/* Admin Pages */}
              <Route path="/admin/orders" element={<OrdersAdminPage />} />
              <Route path="/admin/affiliates" element={<AffiliatesAdminPage />} />
              <Route path="/admin/inventory" element={<InventoryAdminPage />} />
              {/* /cosmos redirects to home — section removed Oct 2026, pending rebuild */}
              <Route path="/cosmos" element={<Navigate to="/" replace />} />
              <Route path="/ikigai" element={<IkigaiPage />} />
            </Routes>
            <ChatBubble />
            <CookieBanner />
          </BrowserRouter>
        </WishlistProvider>
      </CartProvider>
    </div>
  );
}

export default App;
